import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { accountsAPI } from '../lib/api';
import { format, startOfMonth } from 'date-fns';
import {
  BookOpen, ArrowUpRight, ArrowDownRight, TrendingUp, TrendingDown,
  Banknote, CreditCard, Smartphone, Store, Truck, Wallet, RefreshCw,
  Loader2, AlertTriangle, Search, Filter, ChevronDown, ChevronRight,
  Plus, PenLine, Eye, Info, ArrowRight, Landmark, Layers, List,
  BarChart3, CircleDot, CheckCircle2, Clock, X, Building2, ShieldAlert,
  GitCompare, Link2, Unlink, Upload, ThumbsUp, ThumbsDown, ArrowLeftRight,
  FileText, Ban, MessageSquare, Percent, Target
} from 'lucide-react';
import PeriodToolbar from '../components/PeriodToolbar';
import CashProjectionStatusPanel from '../components/CashProjectionStatusPanel';
import { usePeriod } from '../hooks/usePeriod';
import { PERIOD_MODES } from '../lib/period';

// ═══════════════════════════════════════════════════
// FORMATTERS
// ═══════════════════════════════════════════════════

const fmtCur = (v) => {
  if (v === null || v === undefined || isNaN(v)) return '--';
  const abs = Math.abs(v);
  if (abs >= 100000) return `${v < 0 ? '-' : ''}₹${(abs / 100000).toFixed(2)}L`;
  if (abs >= 1000) return `${v < 0 ? '-' : ''}₹${(abs / 1000).toFixed(1)}K`;
  return `₹${v.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

const fmtDate = (d) => {
  if (!d) return '--';
  try { return format(new Date(d + 'T00:00:00'), 'dd MMM yyyy'); } catch { return d; }
};

const fmtDateShort = (d) => {
  if (!d) return '--';
  try { return format(new Date(d + 'T00:00:00'), 'dd MMM'); } catch { return d; }
};

// ═══════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════

const TABS = [
  { key: 'overview', label: 'Overview', icon: BarChart3 },
  { key: 'ledger', label: 'Ledger', icon: List },
  { key: 'reconciliation', label: 'Reconciliation', icon: GitCompare },
  { key: 'obligations', label: 'Obligations', icon: FileText },
  { key: 'forecast', label: 'Forecast', icon: TrendingUp },
  { key: 'accounts', label: 'Accounts', icon: Landmark },
];

const DIRECTION_STYLES = {
  INFLOW: { color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', icon: ArrowUpRight, sign: '+' },
  OUTFLOW: { color: 'text-rose-400', bg: 'bg-rose-500/10', border: 'border-rose-500/30', icon: ArrowDownRight, sign: '-' },
};

const STATUS_STYLES = {
  REALIZED: { color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', label: 'Realized' },
  EXPECTED: { color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20', label: 'Expected' },
};

const MODE_STYLES = {
  CASH: { color: 'text-emerald-400', bg: 'bg-emerald-500/10', icon: Banknote, label: 'Cash' },
  UPI: { color: 'text-blue-400', bg: 'bg-blue-500/10', icon: Smartphone, label: 'UPI' },
  CARD: { color: 'text-violet-400', bg: 'bg-violet-500/10', icon: CreditCard, label: 'Card' },
  BANK: { color: 'text-sky-400', bg: 'bg-sky-500/10', icon: Landmark, label: 'Bank' },
};

const SUBTYPE_LABELS = {
  CASH_SALES: 'Cash Sales',
  UPI_SALES: 'UPI Sales',
  CARD_SALES: 'Card Sales',
  PLATFORM_SETTLEMENT: 'Platform Settlement',
  OTHER_SALES: 'Other Sales',
  VENDOR_PAYMENT: 'Vendor Payment',
  CASH_EXPENSE: 'Cash Expense',
  INVENTORY_VIA_CASH: 'Inventory (Cash)',
  RENT: 'Rent',
  PAYROLL: 'Payroll',
  UTILITIES: 'Utilities',
};

const SOURCE_BADGES = {
  sales: { label: 'Sales', color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  expense: { label: 'Expense', color: 'text-amber-400', bg: 'bg-amber-500/10' },
  accounts: { label: 'Accounts', color: 'text-sky-400', bg: 'bg-sky-500/10' },
};

const ACCOUNT_TYPES = ['BANK', 'CASH', 'WALLET'];

const getModeStyle = (mode) => MODE_STYLES[mode] || MODE_STYLES.BANK;
const getSourceBadge = (engine) => SOURCE_BADGES[engine] || { label: engine || 'Unknown', color: 'text-zinc-400', bg: 'bg-zinc-500/10' };

// ═══════════════════════════════════════════════════
// SMALL COMPONENTS
// ═══════════════════════════════════════════════════

const KpiCard = ({ label, value, sub, icon: Icon, color = 'text-zinc-200', accent }) => (
  <Card className="bg-zinc-900/40 border-zinc-800/40" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider font-medium">{label}</p>
          <p className={`text-xl font-bold ${color}`}>{value}</p>
          {sub && <p className="text-[10px] text-zinc-500">{sub}</p>}
        </div>
        {Icon && (
          <div className={`p-2 rounded-lg ${accent || 'bg-zinc-800/60'}`}>
            <Icon className={`w-4 h-4 ${color} opacity-70`} />
          </div>
        )}
      </div>
    </CardContent>
  </Card>
);

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES.EXPECTED;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold ${s.bg} ${s.color} ${s.border} border`} data-testid={`status-${status}`}>
      {status === 'REALIZED' ? <CheckCircle2 className="w-2.5 h-2.5" /> : <Clock className="w-2.5 h-2.5" />}
      {s.label}
    </span>
  );
};

const ModeBadge = ({ mode }) => {
  const m = getModeStyle(mode);
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium ${m.bg} ${m.color}`}>
      <Icon className="w-2.5 h-2.5" /> {m.label}
    </span>
  );
};

const ProvenanceBadge = ({ engine, type, id }) => {
  const src = getSourceBadge(engine);
  return (
    <div className="flex flex-col gap-0.5" data-testid="provenance-badge">
      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium ${src.bg} ${src.color}`}>
        <Layers className="w-2.5 h-2.5" /> {src.label} / {type}
      </span>
      {id && <span className="text-[8px] text-zinc-600 font-mono truncate max-w-[140px]" title={id}>{id}</span>}
    </div>
  );
};

const EmptyState = ({ icon: Icon, title, description }) => (
  <div className="flex flex-col items-center justify-center py-16 px-6 text-center" data-testid="empty-state">
    <div className="p-4 rounded-2xl bg-zinc-800/40 border border-zinc-700/30 mb-4">
      <Icon className="w-8 h-8 text-zinc-500" />
    </div>
    <h3 className="text-sm font-semibold text-zinc-300 mb-1">{title}</h3>
    <p className="text-xs text-zinc-500 max-w-sm">{description}</p>
  </div>
);

const AttentionCard = ({ items }) => {
  if (!items || items.length === 0) return null;
  const severityIcon = { WARNING: AlertTriangle, INFO: Info };
  const severityColor = { WARNING: 'text-amber-400 bg-amber-500/10 border-amber-500/20', INFO: 'text-sky-400 bg-sky-500/10 border-sky-500/20' };
  return (
    <Card className="bg-zinc-900/40 border-zinc-800/40" data-testid="attention-card">
      <CardHeader className="pb-2 pt-3 px-4">
        <CardTitle className="text-xs font-semibold text-zinc-400 flex items-center gap-1.5">
          <ShieldAlert className="w-3.5 h-3.5 text-amber-400" /> Attention Items
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-3 space-y-2">
        {items.map((item, i) => {
          const Ic = severityIcon[item.severity] || Info;
          const cls = severityColor[item.severity] || severityColor.INFO;
          return (
            <div key={`${item.type}-${i}`} className={`flex items-start gap-2 px-3 py-2 rounded-lg border ${cls}`}>
              <Ic className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span className="text-[11px]">{item.message}</span>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

// ═══════════════════════════════════════════════════
// OVERVIEW TAB
// ═══════════════════════════════════════════════════

const OverviewTab = ({ data, loading }) => {
  const trend = useMemo(() => data?.daily_trend || [], [data?.daily_trend]);
  const maxDay = useMemo(() => {
    if (!trend.length) return 1;
    return Math.max(...trend.map(d => Math.max(
      (d.realized_inflow || 0) + (d.expected_inflow || 0),
      (d.realized_outflow || 0) + (d.expected_outflow || 0)
    )), 1);
  }, [trend]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>;
  if (!data) return <EmptyState icon={BarChart3} title="No Cash Flow Data" description="No cash movements found for the selected period. Try expanding the date range or rebuilding movements." />;

  const t = data.totals || {};
  const inflowComp = data.inflow_composition || [];
  const outflowComp = data.outflow_composition || [];
  const freshness = data.source_freshness || {};

  return (
    <div className="space-y-4">
      {/* KPI Strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="overview-kpis">
        <KpiCard
          label="Realized Inflow"
          value={fmtCur(t.realized_inflow)}
          sub={`${t.inflow_count || 0} transactions`}
          icon={ArrowUpRight}
          color="text-emerald-400"
          accent="bg-emerald-500/10"
        />
        <KpiCard
          label="Expected Inflow"
          value={fmtCur(t.expected_inflow)}
          sub="Awaiting settlement/bank"
          icon={Clock}
          color="text-amber-400"
          accent="bg-amber-500/10"
        />
        <KpiCard
          label="Realized Outflow"
          value={fmtCur(t.realized_outflow)}
          sub={`${t.outflow_count || 0} payments`}
          icon={ArrowDownRight}
          color="text-rose-400"
          accent="bg-rose-500/10"
        />
        <KpiCard
          label="Net Realized"
          value={fmtCur(t.net_realized)}
          sub={t.net_realized >= 0 ? 'Cash positive' : 'Cash negative'}
          icon={t.net_realized >= 0 ? TrendingUp : TrendingDown}
          color={t.net_realized >= 0 ? 'text-emerald-400' : 'text-rose-400'}
          accent={t.net_realized >= 0 ? 'bg-emerald-500/10' : 'bg-rose-500/10'}
        />
      </div>

      {/* Source Freshness Strip */}
      <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-zinc-900/40 border border-zinc-800/40" data-testid="source-freshness">
        <span className="text-[10px] text-zinc-500 uppercase font-medium">Data Sources</span>
        {Object.entries(freshness).map(([src, ok]) => (
          <span key={src} className={`flex items-center gap-1 text-[10px] ${ok ? 'text-emerald-400' : 'text-zinc-600'}`}>
            <CircleDot className="w-2.5 h-2.5" /> {src}
          </span>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Inflow Composition */}
        <Card className="bg-zinc-900/40 border-zinc-800/40" data-testid="inflow-composition">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="text-xs font-semibold text-zinc-400 flex items-center gap-1.5">
              <ArrowUpRight className="w-3.5 h-3.5 text-emerald-400" /> Inflow Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-3 space-y-1.5">
            {inflowComp.length === 0 && <p className="text-[11px] text-zinc-600 py-2">No inflow data</p>}
            {inflowComp.map((c, i) => {
              const pct = t.total_inflow > 0 ? (c.amount / t.total_inflow * 100) : 0;
              return (
                <div key={`${c.subtype}-${c.status}-${i}`} className="flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-[10px] text-zinc-300 truncate">{SUBTYPE_LABELS[c.subtype] || c.subtype}</span>
                      <span className="text-[10px] text-zinc-400 ml-2">{fmtCur(c.amount)}</span>
                    </div>
                    <div className="w-full h-1.5 rounded-full bg-zinc-800 overflow-hidden">
                      <div
                        className={`h-full rounded-full ${c.status === 'REALIZED' ? 'bg-emerald-500' : 'bg-amber-500/50'}`}
                        style={{ width: `${Math.min(pct, 100)}%` }}
                      />
                    </div>
                  </div>
                  <StatusBadge status={c.status} />
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Outflow Composition */}
        <Card className="bg-zinc-900/40 border-zinc-800/40" data-testid="outflow-composition">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="text-xs font-semibold text-zinc-400 flex items-center gap-1.5">
              <ArrowDownRight className="w-3.5 h-3.5 text-rose-400" /> Outflow Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-3 space-y-1.5">
            {outflowComp.length === 0 && (
              <div className="py-3 text-center">
                <p className="text-[11px] text-zinc-600">No expense/payment outflows</p>
                <p className="text-[9px] text-zinc-700 mt-0.5">Sync expenses or record payments to see outflows</p>
              </div>
            )}
            {outflowComp.map((c, i) => {
              const pct = t.total_outflow > 0 ? (c.amount / t.total_outflow * 100) : 0;
              return (
                <div key={`${c.subtype}-${c.status}-${i}`} className="flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-[10px] text-zinc-300 truncate">{SUBTYPE_LABELS[c.subtype] || c.subtype}</span>
                      <span className="text-[10px] text-zinc-400 ml-2">{fmtCur(c.amount)}</span>
                    </div>
                    <div className="w-full h-1.5 rounded-full bg-zinc-800 overflow-hidden">
                      <div className="h-full rounded-full bg-rose-500" style={{ width: `${Math.min(pct, 100)}%` }} />
                    </div>
                  </div>
                  <StatusBadge status={c.status} />
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Attention Items */}
        <div className="space-y-3">
          <AttentionCard items={data.attention} />
          {/* Net Movement Summary */}
          <Card className="bg-zinc-900/40 border-zinc-800/40" data-testid="net-summary">
            <CardContent className="p-4 space-y-3">
              <p className="text-[10px] text-zinc-500 uppercase font-medium">Net Movement Summary</p>
              <div className="space-y-2">
                <div className="flex justify-between text-[11px]">
                  <span className="text-zinc-400">Total Inflow</span>
                  <span className="text-emerald-400 font-medium">{fmtCur(t.total_inflow)}</span>
                </div>
                <div className="flex justify-between text-[11px]">
                  <span className="text-zinc-400">Total Outflow</span>
                  <span className="text-rose-400 font-medium">{fmtCur(t.total_outflow)}</span>
                </div>
                <div className="h-px bg-zinc-800" />
                <div className="flex justify-between text-[11px]">
                  <span className="text-zinc-300 font-medium">Net Movement</span>
                  <span className={`font-bold ${t.net_movement >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>{fmtCur(t.net_movement)}</span>
                </div>
                <div className="flex justify-between text-[11px]">
                  <span className="text-zinc-300 font-medium">Net Realized</span>
                  <span className={`font-bold ${t.net_realized >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>{fmtCur(t.net_realized)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Daily Trend */}
      {trend.length > 0 && (
        <Card className="bg-zinc-900/40 border-zinc-800/40" data-testid="daily-trend">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="text-xs font-semibold text-zinc-400">Daily Cash Flow Trend</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-3">
            <div className="space-y-1">
              {/* Legend */}
              <div className="flex items-center gap-4 mb-2">
                <span className="flex items-center gap-1 text-[9px] text-zinc-500"><span className="w-2 h-2 rounded-sm bg-emerald-500 inline-block" /> Realized In</span>
                <span className="flex items-center gap-1 text-[9px] text-zinc-500"><span className="w-2 h-2 rounded-sm bg-emerald-500/30 inline-block" /> Expected In</span>
                <span className="flex items-center gap-1 text-[9px] text-zinc-500"><span className="w-2 h-2 rounded-sm bg-rose-500 inline-block" /> Realized Out</span>
                <span className="flex items-center gap-1 text-[9px] text-zinc-500"><span className="w-2 h-2 rounded-sm bg-rose-500/30 inline-block" /> Expected Out</span>
              </div>
              {trend.map((d) => {
                const totalIn = (d.realized_inflow || 0) + (d.expected_inflow || 0);
                const totalOut = (d.realized_outflow || 0) + (d.expected_outflow || 0);
                const realInPct = (d.realized_inflow || 0) / maxDay * 100;
                const expInPct = (d.expected_inflow || 0) / maxDay * 100;
                const realOutPct = (d.realized_outflow || 0) / maxDay * 100;
                const expOutPct = (d.expected_outflow || 0) / maxDay * 100;
                return (
                  <div key={d.date} className="flex items-center gap-2 py-1 hover:bg-zinc-800/30 rounded px-1 group transition-colors">
                    <span className="text-[10px] text-zinc-500 w-14 flex-shrink-0 font-mono">{fmtDateShort(d.date)}</span>
                    <div className="flex-1 flex flex-col gap-0.5">
                      {/* Inflow bar */}
                      <div className="h-2 flex rounded-sm overflow-hidden bg-zinc-800/40">
                        <div className="bg-emerald-500 h-full" style={{ width: `${realInPct}%` }} />
                        <div className="bg-emerald-500/30 h-full" style={{ width: `${expInPct}%` }} />
                      </div>
                      {/* Outflow bar */}
                      <div className="h-2 flex rounded-sm overflow-hidden bg-zinc-800/40">
                        <div className="bg-rose-500 h-full" style={{ width: `${realOutPct}%` }} />
                        <div className="bg-rose-500/30 h-full" style={{ width: `${expOutPct}%` }} />
                      </div>
                    </div>
                    <div className="w-28 flex-shrink-0 text-right opacity-0 group-hover:opacity-100 transition-opacity">
                      <span className="text-[9px] text-emerald-400">{fmtCur(totalIn)}</span>
                      <span className="text-[9px] text-zinc-600 mx-1">/</span>
                      <span className="text-[9px] text-rose-400">{fmtCur(totalOut)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════
// LEDGER TAB
// ═══════════════════════════════════════════════════

const LedgerTab = ({ data, loading, filters, setFilters }) => {
  const [expandedDates, setExpandedDates] = useState({});

  const toggleDate = useCallback((date) => {
    setExpandedDates(prev => ({ ...prev, [date]: !prev[date] }));
  }, []);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>;
  if (!data || !data.groups || data.groups.length === 0) {
    return <EmptyState icon={List} title="No Ledger Entries" description="No cash movements found for the selected period and filters. Try expanding the date range or changing filters." />;
  }

  return (
    <div className="space-y-3">
      {/* Filter Strip */}
      <div className="flex flex-wrap items-center gap-2" data-testid="ledger-filters">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
          <Input
            placeholder="Search counterparty, source ID..."
            className="pl-8 h-8 text-xs bg-zinc-900/60 border-zinc-800"
            value={filters.search || ''}
            onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
            data-testid="ledger-search"
          />
        </div>
        {['ALL', 'INFLOW', 'OUTFLOW'].map((d) => (
          <button
            key={d}
            onClick={() => setFilters(f => ({ ...f, direction: d === 'ALL' ? '' : d }))}
            className={`px-2.5 py-1 rounded text-[10px] font-medium border transition-colors ${
              (filters.direction || '') === (d === 'ALL' ? '' : d)
                ? 'bg-zinc-700 text-zinc-200 border-zinc-600'
                : 'bg-zinc-900/40 text-zinc-500 border-zinc-800/40 hover:border-zinc-700'
            }`}
            data-testid={`filter-direction-${d.toLowerCase()}`}
          >
            {d}
          </button>
        ))}
        {['ALL', 'REALIZED', 'EXPECTED'].map((s) => (
          <button
            key={s}
            onClick={() => setFilters(f => ({ ...f, status: s === 'ALL' ? '' : s }))}
            className={`px-2.5 py-1 rounded text-[10px] font-medium border transition-colors ${
              (filters.status || '') === (s === 'ALL' ? '' : s)
                ? 'bg-zinc-700 text-zinc-200 border-zinc-600'
                : 'bg-zinc-900/40 text-zinc-500 border-zinc-800/40 hover:border-zinc-700'
            }`}
            data-testid={`filter-status-${s.toLowerCase()}`}
          >
            {s}
          </button>
        ))}
      </div>

      {/* Summary Strip */}
      <div className="flex items-center gap-4 px-3 py-2 rounded-lg bg-zinc-900/40 border border-zinc-800/40 text-[10px]" data-testid="ledger-summary">
        <span className="text-zinc-500">{data.total_entries} entries</span>
        <span className="text-emerald-400">In: {fmtCur(data.total_inflow)}</span>
        <span className="text-rose-400">Out: {fmtCur(data.total_outflow)}</span>
        <span className={`font-medium ${data.net >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
          Net: {fmtCur(data.net)}
        </span>
        <span className="text-zinc-600 ml-auto">Realized Net: {fmtCur(data.net_realized)}</span>
      </div>

      {/* Day Groups */}
      {data.groups.map((group) => {
        const isExpanded = expandedDates[group.date] !== false; // default expanded
        return (
          <Card key={group.date} className="bg-zinc-900/40 border-zinc-800/40" data-testid={`ledger-group-${group.date}`}>
            {/* Day Header */}
            <button
              onClick={() => toggleDate(group.date)}
              className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-zinc-800/20 transition-colors rounded-t-lg"
              data-testid={`ledger-toggle-${group.date}`}
            >
              <div className="flex items-center gap-3">
                {isExpanded ? <ChevronDown className="w-3.5 h-3.5 text-zinc-500" /> : <ChevronRight className="w-3.5 h-3.5 text-zinc-500" />}
                <span className="text-sm font-semibold text-zinc-300">{fmtDate(group.date)}</span>
                <Badge variant="outline" className="text-[9px] border-zinc-700 text-zinc-500">{group.count} entries</Badge>
              </div>
              <div className="flex items-center gap-3 text-[10px]">
                <span className="text-emerald-400">+{fmtCur(group.inflow)}</span>
                <span className="text-rose-400">-{fmtCur(group.outflow)}</span>
                <span className={`font-semibold ${group.net >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                  Net: {fmtCur(group.net)}
                </span>
              </div>
            </button>

            {/* Entries */}
            {isExpanded && (
              <CardContent className="px-4 pb-3 pt-0">
                <div className="border-t border-zinc-800/40 pt-2">
                  <table className="w-full">
                    <thead>
                      <tr className="text-[9px] text-zinc-600 uppercase">
                        <th className="text-left py-1 font-medium">Direction</th>
                        <th className="text-left py-1 font-medium">Status</th>
                        <th className="text-left py-1 font-medium">Mode</th>
                        <th className="text-left py-1 font-medium">Subtype</th>
                        <th className="text-left py-1 font-medium">Counterparty</th>
                        <th className="text-right py-1 font-medium">Amount</th>
                        <th className="text-left py-1 font-medium">Provenance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.entries.map((entry) => {
                        const dir = DIRECTION_STYLES[entry.direction] || DIRECTION_STYLES.INFLOW;
                        const DirIcon = dir.icon;
                        return (
                          <tr key={entry.cash_event_id} className="border-t border-zinc-800/20 hover:bg-zinc-800/20 transition-colors" data-testid={`ledger-row-${entry.cash_event_id}`}>
                            <td className="py-1.5">
                              <span className={`inline-flex items-center gap-1 text-[10px] font-medium ${dir.color}`}>
                                <DirIcon className="w-3 h-3" /> {entry.direction}
                              </span>
                            </td>
                            <td className="py-1.5"><StatusBadge status={entry.status} /></td>
                            <td className="py-1.5"><ModeBadge mode={entry.mode} /></td>
                            <td className="py-1.5">
                              <span className="text-[10px] text-zinc-400">{SUBTYPE_LABELS[entry.subtype] || entry.subtype}</span>
                            </td>
                            <td className="py-1.5">
                              <span className="text-[10px] text-zinc-300">{entry.counterparty}</span>
                            </td>
                            <td className="py-1.5 text-right">
                              <span className={`text-xs font-semibold ${dir.color}`}>
                                {dir.sign}{fmtCur(entry.amount)}
                              </span>
                            </td>
                            <td className="py-1.5">
                              <ProvenanceBadge engine={entry.source_engine} type={entry.source_type} id={entry.source_id} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            )}
          </Card>
        );
      })}
    </div>
  );
};

// ═══════════════════════════════════════════════════
// RECONCILIATION TAB
// ═══════════════════════════════════════════════════

const MATCH_STATUS_STYLES = {
  EXACT: { color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', icon: CheckCircle2 },
  NEAR_EXACT: { color: 'text-emerald-300', bg: 'bg-emerald-500/8', border: 'border-emerald-500/15', icon: CheckCircle2 },
  PARTIAL: { color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20', icon: AlertTriangle },
  SUGGESTED: { color: 'text-sky-400', bg: 'bg-sky-500/10', border: 'border-sky-500/20', icon: Target },
  MATCHED: { color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', icon: Link2 },
  CONFIRMED: { color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', icon: CheckCircle2 },
  IGNORED: { color: 'text-zinc-500', bg: 'bg-zinc-500/10', border: 'border-zinc-500/20', icon: Ban },
  TRANSFER: { color: 'text-violet-400', bg: 'bg-violet-500/10', border: 'border-violet-500/20', icon: ArrowLeftRight },
  UNMATCHED: { color: 'text-rose-400', bg: 'bg-rose-500/10', border: 'border-rose-500/20', icon: Unlink },
};

const getMatchStyle = (status) => MATCH_STATUS_STYLES[status] || MATCH_STATUS_STYLES.UNMATCHED;

// ── Suggested Match Card ──
const SuggestedMatchCard = ({ match, onConfirm, onIgnore, onTransfer }) => {
  const [showIgnoreForm, setShowIgnoreForm] = useState(false);
  const [ignoreReason, setIgnoreReason] = useState('');
  const [showTransferForm, setShowTransferForm] = useState(false);
  const [transferFrom, setTransferFrom] = useState('');
  const [transferTo, setTransferTo] = useState('');

  const ms = getMatchStyle(match.match_type);
  const MatchIcon = ms.icon;
  const sys = match.system_side || {};
  const bank = match.bank_side || {};
  const confPct = Math.round((match.confidence || 0) * 100);

  return (
    <Card className="bg-zinc-900/40 border-zinc-800/40" data-testid={`match-card-${match.reconciliation_id}`}>
      <CardContent className="p-4 space-y-3">
        {/* Match Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`p-1.5 rounded-md ${ms.bg}`}>
              <MatchIcon className={`w-3.5 h-3.5 ${ms.color}`} />
            </div>
            <div>
              <span className={`text-xs font-semibold ${ms.color}`}>{match.match_type}</span>
              <span className="text-[9px] text-zinc-500 ml-2">{match.layer === 'LAYER_1' ? 'Payment ↔ Bank Debit' : 'Settlement ↔ Bank Credit'}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${confPct >= 90 ? 'bg-emerald-500/20 text-emerald-400' : confPct >= 70 ? 'bg-amber-500/20 text-amber-400' : 'bg-rose-500/20 text-rose-400'}`}>
              {confPct}% match
            </div>
          </div>
        </div>

        {/* Comparison Grid */}
        <div className="grid grid-cols-2 gap-3">
          {/* System Side */}
          <div className="rounded-lg bg-zinc-800/30 border border-zinc-700/30 p-3 space-y-1.5">
            <p className="text-[9px] text-zinc-500 uppercase font-semibold flex items-center gap-1">
              <Layers className="w-2.5 h-2.5" /> System Record
            </p>
            <p className="text-sm font-bold text-zinc-200">{fmtCur(sys.amount)}</p>
            <div className="space-y-0.5 text-[10px]">
              <p className="text-zinc-400">{fmtDate(sys.date)}</p>
              <p className="text-zinc-400">{SUBTYPE_LABELS[sys.subtype] || sys.subtype}</p>
              <p className="text-zinc-500">{sys.counterparty}</p>
              <ProvenanceBadge engine={sys.source_engine} type={sys.source_type} id={sys.source_id} />
            </div>
          </div>
          {/* Bank Side */}
          <div className="rounded-lg bg-zinc-800/30 border border-zinc-700/30 p-3 space-y-1.5">
            <p className="text-[9px] text-zinc-500 uppercase font-semibold flex items-center gap-1">
              <Landmark className="w-2.5 h-2.5" /> Bank Record
            </p>
            <p className="text-sm font-bold text-zinc-200">{fmtCur(bank.amount)}</p>
            <div className="space-y-0.5 text-[10px]">
              <p className="text-zinc-400">{fmtDate(bank.date)}</p>
              <p className="text-zinc-400">{bank.description}</p>
              <p className="text-zinc-500">{bank.reference ? `Ref: ${bank.reference}` : 'No reference'}</p>
              {bank.bank_account && <p className="text-zinc-600">Account: {bank.bank_account}</p>}
            </div>
          </div>
        </div>

        {/* Variance Strip */}
        <div className={`flex items-center justify-between px-3 py-1.5 rounded-md ${Math.abs(match.variance) < 1 ? 'bg-emerald-500/5 border border-emerald-500/20' : 'bg-amber-500/5 border border-amber-500/20'}`}>
          <span className="text-[10px] text-zinc-400">Variance</span>
          <div className="flex items-center gap-2">
            <span className={`text-xs font-semibold ${Math.abs(match.variance) < 1 ? 'text-emerald-400' : 'text-amber-400'}`}>
              {match.variance >= 0 ? '+' : ''}{fmtCur(match.variance)}
            </span>
            {match.variance_pct !== 0 && (
              <span className="text-[9px] text-zinc-500">({match.variance_pct}%)</span>
            )}
          </div>
        </div>

        {/* Reason */}
        <p className="text-[10px] text-zinc-500 italic">{match.reason}</p>

        {/* Actions */}
        <div className="flex items-center gap-2 pt-1">
          <Button size="sm" className="h-6 text-[10px] gap-1 bg-emerald-600 hover:bg-emerald-700" onClick={() => onConfirm(match.reconciliation_id)} data-testid={`confirm-match-${match.reconciliation_id}`}>
            <ThumbsUp className="w-2.5 h-2.5" /> Confirm Match
          </Button>
          <Button size="sm" variant="outline" className="h-6 text-[10px] gap-1 border-zinc-700" onClick={() => setShowTransferForm(!showTransferForm)} data-testid={`mark-transfer-${match.reconciliation_id}`}>
            <ArrowLeftRight className="w-2.5 h-2.5" /> Transfer
          </Button>
          <Button size="sm" variant="outline" className="h-6 text-[10px] gap-1 border-zinc-700 text-zinc-500" onClick={() => setShowIgnoreForm(!showIgnoreForm)} data-testid={`ignore-match-${match.reconciliation_id}`}>
            <Ban className="w-2.5 h-2.5" /> Ignore
          </Button>
        </div>

        {/* Ignore Form */}
        {showIgnoreForm && (
          <div className="flex items-center gap-2 pt-1" data-testid="ignore-form">
            <Input className="h-7 text-xs bg-zinc-900/60 border-zinc-800 flex-1" placeholder="Reason for ignoring..." value={ignoreReason} onChange={(e) => setIgnoreReason(e.target.value)} />
            <Button size="sm" className="h-7 text-xs" onClick={() => { onIgnore(match.reconciliation_id, ignoreReason); setShowIgnoreForm(false); }}>Submit</Button>
          </div>
        )}

        {/* Transfer Form */}
        {showTransferForm && (
          <div className="space-y-2 pt-1" data-testid="transfer-form">
            <div className="flex items-center gap-2">
              <Input className="h-7 text-xs bg-zinc-900/60 border-zinc-800" placeholder="From account" value={transferFrom} onChange={(e) => setTransferFrom(e.target.value)} />
              <ArrowRight className="w-3 h-3 text-zinc-600 flex-shrink-0" />
              <Input className="h-7 text-xs bg-zinc-900/60 border-zinc-800" placeholder="To account" value={transferTo} onChange={(e) => setTransferTo(e.target.value)} />
            </div>
            <Button size="sm" className="h-7 text-xs" onClick={() => { onTransfer(match.reconciliation_id, transferFrom, transferTo); setShowTransferForm(false); }}>Mark as Transfer</Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ── Unmatched Row ──
const UnmatchedRow = ({ item, type, onIgnore }) => {
  const [showIgnore, setShowIgnore] = useState(false);
  const [reason, setReason] = useState('');

  const isBank = type === 'bank';
  const dir = isBank ? item.direction : item.direction;
  const dirStyle = DIRECTION_STYLES[dir] || DIRECTION_STYLES.INFLOW;
  const DirIcon = dirStyle.icon;

  return (
    <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-zinc-900/20 border border-zinc-800/30 hover:bg-zinc-800/20 transition-colors" data-testid={`unmatched-${type}-${isBank ? item.bank_entry_id : item.cash_event_id}`}>
      <div className={`p-1.5 rounded-md ${dirStyle.bg}`}>
        <DirIcon className={`w-3 h-3 ${dirStyle.color}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-zinc-200">{fmtCur(item.amount)}</span>
          <span className="text-[9px] text-zinc-500">{fmtDate(isBank ? item.entry_date : item.event_date)}</span>
          {!isBank && <ModeBadge mode={item.mode} />}
          {!isBank && <StatusBadge status={item.status} />}
        </div>
        <p className="text-[10px] text-zinc-400 truncate">
          {isBank ? (item.description || item.reference || 'No description') : (SUBTYPE_LABELS[item.subtype] || item.subtype)}
          {!isBank && item.counterparty && ` — ${item.counterparty}`}
        </p>
        {!isBank && (
          <ProvenanceBadge engine={item.source_engine} type={item.source_type} id={item.source_id} />
        )}
        {isBank && item.reference && (
          <p className="text-[9px] text-zinc-600 font-mono">Ref: {item.reference}</p>
        )}
      </div>
      <Button size="sm" variant="ghost" className="h-6 text-[10px] text-zinc-500" onClick={() => setShowIgnore(!showIgnore)}>
        <Ban className="w-2.5 h-2.5 mr-1" /> Ignore
      </Button>
      {showIgnore && (
        <div className="flex items-center gap-1">
          <Input className="h-6 text-[10px] bg-zinc-900/60 border-zinc-800 w-32" placeholder="Reason..." value={reason} onChange={(e) => setReason(e.target.value)} />
          <Button size="sm" className="h-6 text-[10px]" onClick={() => {
            onIgnore(isBank ? [] : [item.cash_event_id], isBank ? [item.bank_entry_id] : [], reason);
            setShowIgnore(false);
          }}>OK</Button>
        </div>
      )}
    </div>
  );
};

// ── Bank Entry Upload Modal ──
const BankUploadPanel = ({ onImport }) => {
  const [showManual, setShowManual] = useState(false);
  const [manualEntry, setManualEntry] = useState({ entry_date: '', direction: 'DEBIT', amount: '', description: '', reference: '', bank_account: '' });
  const [csvRows, setCsvRows] = useState(null);
  const [csvFile, setCsvFile] = useState(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCsvFile(file);

    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      const lines = text.split('\n').filter(l => l.trim());
      if (lines.length < 2) { toast.error('CSV must have header + data rows'); return; }

      const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g, '_'));
      const rows = [];
      for (let i = 1; i < lines.length; i++) {
        const vals = lines[i].split(',').map(v => v.trim());
        const row = {};
        headers.forEach((h, idx) => { row[h] = vals[idx] || ''; });
        rows.push(row);
      }
      setCsvRows(rows);
      toast.success(`Parsed ${rows.length} rows from CSV`);
    };
    reader.readAsText(file);
  };

  const handleImportCsv = () => {
    if (!csvRows || csvRows.length === 0) return;
    onImport(csvRows);
    setCsvRows(null);
    setCsvFile(null);
  };

  const handleAddManual = () => {
    if (!manualEntry.entry_date || !manualEntry.amount) {
      toast.error('Date and amount are required');
      return;
    }
    onImport([{
      date: manualEntry.entry_date,
      [manualEntry.direction.toLowerCase()]: parseFloat(manualEntry.amount),
      description: manualEntry.description,
      reference: manualEntry.reference,
      bank_account: manualEntry.bank_account,
    }]);
    setManualEntry({ entry_date: '', direction: 'DEBIT', amount: '', description: '', reference: '', bank_account: '' });
    setShowManual(false);
  };

  return (
    <Card className="bg-zinc-900/40 border-zinc-800/40" data-testid="bank-upload-panel">
      <CardHeader className="pb-2 pt-3 px-4">
        <CardTitle className="text-xs font-semibold text-zinc-400 flex items-center gap-1.5">
          <Upload className="w-3.5 h-3.5 text-sky-400" /> Bank Statement Data
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-3 space-y-3">
        <p className="text-[10px] text-zinc-500">Upload bank statement CSV or add entries manually to match against system movements.</p>

        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-zinc-800 text-zinc-300 hover:bg-zinc-700 cursor-pointer transition-colors border border-zinc-700/40" data-testid="csv-upload-btn">
            <FileText className="w-3 h-3" /> Upload CSV
            <input type="file" accept=".csv" className="hidden" onChange={handleFileChange} />
          </label>
          <Button size="sm" variant="outline" className="h-7 text-xs gap-1 border-zinc-700" onClick={() => setShowManual(!showManual)} data-testid="add-manual-btn">
            <Plus className="w-3 h-3" /> Add Manually
          </Button>
        </div>

        {/* CSV Preview */}
        {csvRows && (
          <div className="space-y-2" data-testid="csv-preview">
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-zinc-400">{csvRows.length} rows parsed from {csvFile?.name}</span>
              <Button size="sm" className="h-6 text-[10px]" onClick={handleImportCsv} data-testid="import-csv-btn">Import {csvRows.length} Entries</Button>
            </div>
            <div className="max-h-32 overflow-auto rounded border border-zinc-800 text-[9px] font-mono text-zinc-400">
              <table className="w-full">
                <thead><tr className="bg-zinc-800/60">{Object.keys(csvRows[0]).map(h => <th key={h} className="px-2 py-1 text-left">{h}</th>)}</tr></thead>
                <tbody>{csvRows.slice(0, 5).map((r, i) => <tr key={i} className="border-t border-zinc-800/30">{Object.values(r).map((v, j) => <td key={j} className="px-2 py-0.5">{v}</td>)}</tr>)}</tbody>
              </table>
              {csvRows.length > 5 && <p className="px-2 py-1 text-zinc-600">...and {csvRows.length - 5} more rows</p>}
            </div>
          </div>
        )}

        {/* Manual Entry Form */}
        {showManual && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="manual-entry-form">
            <div>
              <label className="text-[9px] text-zinc-500 uppercase mb-0.5 block">Date</label>
              <input type="date" className="w-full h-7 text-xs bg-zinc-900/60 border border-zinc-800 rounded px-2 text-zinc-300" value={manualEntry.entry_date} onChange={(e) => setManualEntry(m => ({ ...m, entry_date: e.target.value }))} />
            </div>
            <div>
              <label className="text-[9px] text-zinc-500 uppercase mb-0.5 block">Direction</label>
              <div className="flex gap-1">
                {['DEBIT', 'CREDIT'].map(d => (
                  <button key={d} onClick={() => setManualEntry(m => ({ ...m, direction: d }))}
                    className={`px-2.5 py-1 rounded text-[10px] font-medium border transition-colors ${manualEntry.direction === d ? 'bg-zinc-700 text-zinc-200 border-zinc-600' : 'bg-zinc-900/40 text-zinc-500 border-zinc-800/40'}`}>{d}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[9px] text-zinc-500 uppercase mb-0.5 block">Amount</label>
              <Input className="h-7 text-xs bg-zinc-900/60 border-zinc-800" type="number" value={manualEntry.amount} onChange={(e) => setManualEntry(m => ({ ...m, amount: e.target.value }))} />
            </div>
            <div>
              <label className="text-[9px] text-zinc-500 uppercase mb-0.5 block">Description</label>
              <Input className="h-7 text-xs bg-zinc-900/60 border-zinc-800" value={manualEntry.description} onChange={(e) => setManualEntry(m => ({ ...m, description: e.target.value }))} />
            </div>
            <div>
              <label className="text-[9px] text-zinc-500 uppercase mb-0.5 block">Reference/UTR</label>
              <Input className="h-7 text-xs bg-zinc-900/60 border-zinc-800" value={manualEntry.reference} onChange={(e) => setManualEntry(m => ({ ...m, reference: e.target.value }))} />
            </div>
            <div>
              <label className="text-[9px] text-zinc-500 uppercase mb-0.5 block">Bank Account</label>
              <Input className="h-7 text-xs bg-zinc-900/60 border-zinc-800" value={manualEntry.bank_account} onChange={(e) => setManualEntry(m => ({ ...m, bank_account: e.target.value }))} />
            </div>
            <div className="col-span-2 md:col-span-3 flex gap-2">
              <Button size="sm" className="h-7 text-xs" onClick={handleAddManual} data-testid="save-manual-entry">Save Entry</Button>
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setShowManual(false)}>Cancel</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ── Main Reconciliation Tab ──
const ReconciliationTab = ({ dateFrom, dateTo }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState('suggested');

  const fetchRecon = useCallback(async () => {
    if (!dateFrom || !dateTo) return;
    setLoading(true);
    try {
      const res = await accountsAPI.getCashflowReconciliation({ date_from: dateFrom, date_to: dateTo });
      setData(res.data);
    } catch (err) {
      console.error('Reconciliation fetch error:', err);
      toast.error('Failed to load reconciliation data');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => { fetchRecon(); }, [fetchRecon]);

  const handleConfirm = async (reconciliationId) => {
    try {
      await accountsAPI.confirmMatch({ reconciliation_id: reconciliationId });
      toast.success('Match confirmed');
      fetchRecon();
    } catch { toast.error('Failed to confirm match'); }
  };

  const handleIgnoreSuggested = async (reconciliationId, reason) => {
    try {
      await accountsAPI.ignoreEntry({ reconciliation_id: reconciliationId, reason });
      toast.success('Entry ignored');
      fetchRecon();
    } catch { toast.error('Failed to ignore'); }
  };

  const handleIgnoreRaw = async (cashEventIds, bankEntryIds, reason) => {
    try {
      await accountsAPI.ignoreEntry({ cash_event_ids: cashEventIds, bank_entry_ids: bankEntryIds, reason });
      toast.success('Entry ignored');
      fetchRecon();
    } catch { toast.error('Failed to ignore'); }
  };

  const handleTransfer = async (reconciliationId, fromAccount, toAccount) => {
    try {
      await accountsAPI.markTransfer({ reconciliation_id: reconciliationId, from_account: fromAccount, to_account: toAccount });
      toast.success('Marked as transfer');
      fetchRecon();
    } catch { toast.error('Failed to mark transfer'); }
  };

  const handleImportBankEntries = async (rows) => {
    try {
      const res = await accountsAPI.importBankEntries({ rows });
      toast.success(`Imported ${res.data.imported} bank entries`);
      fetchRecon();
    } catch { toast.error('Failed to import bank entries'); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>;

  const summary = data?.summary || {};
  const suggested = data?.suggested_matches || [];
  const unmatchedDebits = data?.unmatched_system_debits || [];
  const unmatchedCredits = data?.unmatched_system_credits || [];
  const unmatchedBankDebits = data?.unmatched_bank_debits || [];
  const unmatchedBankCredits = data?.unmatched_bank_credits || [];
  const totals = summary.totals || {};

  const sections = [
    { key: 'suggested', label: 'Suggested Matches', count: suggested.length, icon: Target, color: 'text-sky-400' },
    { key: 'unmatched_debits', label: 'Unmatched Debits', count: unmatchedDebits.length, icon: ArrowDownRight, color: 'text-rose-400' },
    { key: 'unmatched_credits', label: 'Unmatched Credits', count: unmatchedCredits.length, icon: ArrowUpRight, color: 'text-amber-400' },
    { key: 'bank_unmatched', label: 'Bank Unmatched', count: unmatchedBankDebits.length + unmatchedBankCredits.length, icon: Landmark, color: 'text-zinc-400' },
    { key: 'bank_upload', label: 'Upload Bank Data', count: null, icon: Upload, color: 'text-sky-400' },
  ];

  return (
    <div className="space-y-4" data-testid="reconciliation-tab">
      {/* Summary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="recon-summary">
        <KpiCard label="Suggested Matches" value={suggested.length} sub={fmtCur(totals.suggested_match_amount)} icon={Target} color="text-sky-400" accent="bg-sky-500/10" />
        <KpiCard label="Unmatched Debits" value={unmatchedDebits.length} sub={fmtCur(totals.unmatched_debit_amount)} icon={ArrowDownRight} color="text-rose-400" accent="bg-rose-500/10" />
        <KpiCard label="Unmatched Credits" value={unmatchedCredits.length} sub={fmtCur(totals.unmatched_credit_amount)} icon={ArrowUpRight} color="text-amber-400" accent="bg-amber-500/10" />
        <KpiCard label="Bank Unmatched" value={unmatchedBankDebits.length + unmatchedBankCredits.length} sub={fmtCur((totals.bank_unmatched_debit_amount || 0) + (totals.bank_unmatched_credit_amount || 0))} icon={Landmark} color="text-zinc-400" accent="bg-zinc-800/40" />
        <KpiCard label="Already Reconciled" value={summary.already_reconciled || 0} sub="Confirmed or resolved" icon={CheckCircle2} color="text-emerald-400" accent="bg-emerald-500/10" />
      </div>

      {/* Data Status Strip */}
      <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-zinc-900/40 border border-zinc-800/40" data-testid="recon-data-status">
        <span className="text-[10px] text-zinc-500 uppercase font-medium">Reconciliation Status</span>
        <span className="text-[10px] text-zinc-400">{summary.total_movements || 0} movements loaded</span>
        <span className="text-[10px] text-zinc-400">{summary.bank_entries_loaded || 0} bank entries</span>
        {(summary.bank_entries_loaded || 0) === 0 && (
          <span className="flex items-center gap-1 text-[10px] text-amber-400">
            <AlertTriangle className="w-2.5 h-2.5" /> No bank data — upload a statement to begin matching
          </span>
        )}
        <Button size="sm" variant="ghost" className="h-5 text-[9px] text-zinc-500 ml-auto gap-1" onClick={fetchRecon}>
          <RefreshCw className="w-2.5 h-2.5" /> Refresh
        </Button>
      </div>

      {/* Section Navigation */}
      <div className="flex items-center gap-1 flex-wrap" data-testid="recon-sections">
        {sections.map((sec) => {
          const SIcon = sec.icon;
          const active = activeSection === sec.key;
          return (
            <button key={sec.key} onClick={() => setActiveSection(sec.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[10px] font-medium border transition-all ${active ? 'bg-zinc-800 text-zinc-100 border-zinc-600 shadow-sm' : 'bg-zinc-900/40 text-zinc-500 border-zinc-800/40 hover:border-zinc-700'}`}
              data-testid={`section-${sec.key}`}>
              <SIcon className={`w-3 h-3 ${active ? sec.color : 'text-zinc-600'}`} /> {sec.label}
              {sec.count !== null && <Badge variant="outline" className="text-[8px] h-4 px-1 border-zinc-700">{sec.count}</Badge>}
            </button>
          );
        })}
      </div>

      {/* Section Content */}
      {activeSection === 'suggested' && (
        <div className="space-y-3" data-testid="suggested-matches-section">
          {suggested.length === 0 ? (
            <EmptyState icon={Target} title="No Suggested Matches"
              description={summary.bank_entries_loaded === 0 ? "Upload bank statement data to enable matching against system records." : "No matches found. All movements may already be reconciled or the matching criteria weren't met."} />
          ) : (
            suggested.map(m => (
              <SuggestedMatchCard key={m.reconciliation_id} match={m} onConfirm={handleConfirm} onIgnore={handleIgnoreSuggested} onTransfer={handleTransfer} />
            ))
          )}
        </div>
      )}

      {activeSection === 'unmatched_debits' && (
        <div className="space-y-2" data-testid="unmatched-debits-section">
          {unmatchedDebits.length === 0 ? (
            <EmptyState icon={ArrowDownRight} title="No Unmatched Debits" description="All payment outflows are matched or awaiting bank data." />
          ) : (
            <>
              <p className="text-[10px] text-zinc-500">{unmatchedDebits.length} system outflows without a matching bank debit</p>
              {unmatchedDebits.map(m => <UnmatchedRow key={m.cash_event_id} item={m} type="system" onIgnore={handleIgnoreRaw} />)}
            </>
          )}
        </div>
      )}

      {activeSection === 'unmatched_credits' && (
        <div className="space-y-2" data-testid="unmatched-credits-section">
          {unmatchedCredits.length === 0 ? (
            <EmptyState icon={ArrowUpRight} title="No Unmatched Credits" description="All expected inflows are matched or awaiting settlement data." />
          ) : (
            <>
              <p className="text-[10px] text-zinc-500">{unmatchedCredits.length} expected inflows without a matching bank credit</p>
              {unmatchedCredits.map(m => <UnmatchedRow key={m.cash_event_id} item={m} type="system" onIgnore={handleIgnoreRaw} />)}
            </>
          )}
        </div>
      )}

      {activeSection === 'bank_unmatched' && (
        <div className="space-y-3" data-testid="bank-unmatched-section">
          {unmatchedBankDebits.length === 0 && unmatchedBankCredits.length === 0 ? (
            <EmptyState icon={Landmark} title="No Unmatched Bank Entries" description="Upload bank statement data to see entries that don't match system records." />
          ) : (
            <>
              {unmatchedBankDebits.length > 0 && (
                <div>
                  <p className="text-[10px] text-zinc-500 mb-2">{unmatchedBankDebits.length} bank debits without system match</p>
                  {unmatchedBankDebits.map(b => <UnmatchedRow key={b.bank_entry_id} item={b} type="bank" onIgnore={handleIgnoreRaw} />)}
                </div>
              )}
              {unmatchedBankCredits.length > 0 && (
                <div className="mt-3">
                  <p className="text-[10px] text-zinc-500 mb-2">{unmatchedBankCredits.length} bank credits without system match</p>
                  {unmatchedBankCredits.map(b => <UnmatchedRow key={b.bank_entry_id} item={b} type="bank" onIgnore={handleIgnoreRaw} />)}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {activeSection === 'bank_upload' && (
        <BankUploadPanel onImport={handleImportBankEntries} />
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════
// OBLIGATIONS TAB
// ═══════════════════════════════════════════════════

const URGENCY_STYLES = {
  OVERDUE: { color: 'text-rose-400', bg: 'bg-rose-500/10', border: 'border-rose-500/20', label: 'Overdue' },
  DUE_7D: { color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20', label: 'Due in 7d' },
  DUE_30D: { color: 'text-sky-400', bg: 'bg-sky-500/10', border: 'border-sky-500/20', label: 'Due in 30d' },
  FUTURE: { color: 'text-zinc-400', bg: 'bg-zinc-500/10', border: 'border-zinc-500/20', label: 'Future' },
};

const ObligationsTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filterUrgency, setFilterUrgency] = useState('');

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const res = await accountsAPI.getObligations();
        setData(res.data);
      } catch { toast.error('Failed to load obligations'); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>;
  if (!data) return <EmptyState icon={FileText} title="No Obligation Data" description="Could not load obligations." />;

  const s = data.summary || {};
  const obligations = (data.obligations || []).filter(o => !filterUrgency || o.urgency === filterUrgency);
  const topVendors = data.top_vendors || [];

  return (
    <div className="space-y-4" data-testid="obligations-tab">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="obligations-kpis">
        <KpiCard label="Total Outstanding" value={fmtCur(s.total_outstanding)} sub={`${s.total_count} obligations`} icon={FileText} color="text-zinc-200" accent="bg-zinc-800/40" />
        <KpiCard label="Overdue" value={fmtCur(s.overdue_amount)} sub={`${s.overdue_count} bills`} icon={AlertTriangle} color="text-rose-400" accent="bg-rose-500/10" />
        <KpiCard label="Due in 7 Days" value={fmtCur(s.upcoming_7d_amount)} icon={Clock} color="text-amber-400" accent="bg-amber-500/10" />
        <KpiCard label="Due in 30 Days" value={fmtCur(s.upcoming_30d_amount)} icon={Target} color="text-sky-400" accent="bg-sky-500/10" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Top Vendors */}
        <Card className="bg-zinc-900/40 border-zinc-800/40" data-testid="top-vendors">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="text-xs font-semibold text-zinc-400 flex items-center gap-1.5">
              <Store className="w-3.5 h-3.5 text-rose-400" /> Top Vendor Obligations
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-3 space-y-1.5">
            {topVendors.length === 0 && <p className="text-[11px] text-zinc-600 py-2">No vendor data</p>}
            {topVendors.map((v, i) => {
              const pct = s.total_outstanding > 0 ? (v.total_outstanding / s.total_outstanding * 100) : 0;
              return (
                <div key={`${v.vendor_name}-${i}`} className="flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-[10px] text-zinc-300 truncate">{v.vendor_name}</span>
                      <span className="text-[10px] text-zinc-400 ml-2">{fmtCur(v.total_outstanding)}</span>
                    </div>
                    <div className="w-full h-1.5 rounded-full bg-zinc-800 overflow-hidden">
                      <div className="h-full rounded-full bg-rose-500/60" style={{ width: `${Math.min(pct, 100)}%` }} />
                    </div>
                  </div>
                  <span className="text-[9px] text-zinc-500 w-8 text-right">{v.bill_count}b</span>
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Obligations List */}
        <div className="lg:col-span-2 space-y-2">
          {/* Filter */}
          <div className="flex items-center gap-1.5" data-testid="obligation-filters">
            {['', 'OVERDUE', 'DUE_7D', 'DUE_30D', 'FUTURE'].map(u => (
              <button key={u || 'ALL'} onClick={() => setFilterUrgency(u)}
                className={`px-2.5 py-1 rounded text-[10px] font-medium border transition-colors ${filterUrgency === u ? 'bg-zinc-700 text-zinc-200 border-zinc-600' : 'bg-zinc-900/40 text-zinc-500 border-zinc-800/40 hover:border-zinc-700'}`}
                data-testid={`filter-urgency-${u || 'all'}`}>
                {u ? (URGENCY_STYLES[u]?.label || u) : 'All'}
              </button>
            ))}
          </div>

          {obligations.length === 0 ? (
            <EmptyState icon={CheckCircle2} title="No Obligations" description={filterUrgency ? "No obligations match this filter." : "All vendor bills are settled."} />
          ) : (
            <div className="space-y-1.5">
              {obligations.map((ob) => {
                const urg = URGENCY_STYLES[ob.urgency] || URGENCY_STYLES.FUTURE;
                return (
                  <div key={ob.obligation_id} className={`flex items-center gap-3 px-3 py-2 rounded-lg bg-zinc-900/20 border ${urg.border} hover:bg-zinc-800/20 transition-colors`} data-testid={`obligation-${ob.obligation_id}`}>
                    <div className={`p-1.5 rounded-md ${urg.bg}`}>
                      <FileText className={`w-3 h-3 ${urg.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-zinc-200">{ob.counterparty}</span>
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[8px] font-semibold ${urg.bg} ${urg.color} border ${urg.border}`}>{urg.label}</span>
                        {ob.due_source !== 'EXPLICIT' && <span className="text-[8px] text-zinc-600 italic">est. due</span>}
                      </div>
                      <p className="text-[10px] text-zinc-500">{ob.source_reference} | Billed: {fmtDate(ob.bill_date)}</p>
                    </div>
                    <div className="text-right">
                      <p className={`text-xs font-semibold ${urg.color}`}>{fmtCur(ob.amount)}</p>
                      <p className="text-[9px] text-zinc-600">Due: {fmtDate(ob.due_date)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════
// FORECAST TAB
// ═══════════════════════════════════════════════════

const ConfidenceBadge = ({ confidence }) => {
  if (!confidence) return null;
  const { score, grade } = confidence;
  const cls = grade === 'HIGH' ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20'
    : grade === 'MEDIUM' ? 'text-amber-400 bg-amber-500/10 border-amber-500/20'
    : 'text-rose-400 bg-rose-500/10 border-rose-500/20';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold border ${cls}`} data-testid="confidence-badge">
      <Target className="w-2.5 h-2.5" /> {score}% {grade}
    </span>
  );
};

const ForecastTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [horizon, setHorizon] = useState(30);

  const fetchForecast = useCallback(async () => {
    setLoading(true);
    try {
      const res = await accountsAPI.getCashForecast({ horizon });
      setData(res.data);
    } catch { toast.error('Failed to load forecast'); }
    finally { setLoading(false); }
  }, [horizon]);

  useEffect(() => { fetchForecast(); }, [fetchForecast]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>;
  if (!data) return <EmptyState icon={TrendingUp} title="No Forecast Data" description="Could not compute cash forecast." />;

  const runway = data.runway || {};
  const stress = data.stress || {};
  const confidence = data.confidence || {};
  const forecast = data.daily_forecast || [];
  const drivers = data.drivers || [];
  const patterns = data.source_patterns || [];
  const oblSum = data.obligations_summary || {};
  const infSum = data.inflows_summary || {};

  // Chart scale
  const maxBal = Math.max(...forecast.map(d => Math.abs(d.closing_balance)), 1);

  return (
    <div className="space-y-4" data-testid="forecast-tab">
      {/* Header Row: Horizon + Confidence */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {[7, 30].map(h => (
            <button key={h} onClick={() => setHorizon(h)}
              className={`px-3 py-1 rounded text-[10px] font-medium border transition-colors ${horizon === h ? 'bg-zinc-700 text-zinc-200 border-zinc-600' : 'bg-zinc-900/40 text-zinc-500 border-zinc-800/40 hover:border-zinc-700'}`}
              data-testid={`horizon-${h}d`}>
              {h} Days
            </button>
          ))}
        </div>
        <ConfidenceBadge confidence={confidence} />
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="forecast-kpis">
        <KpiCard label="Opening Cash" value={fmtCur(data.opening_cash)} sub="Realized position" icon={Wallet} color="text-zinc-200" accent="bg-zinc-800/40" />
        <KpiCard label="Projected Inflows" value={fmtCur(infSum.total_projected_inflow)} sub={`${infSum.source_count} sources`} icon={ArrowUpRight} color="text-emerald-400" accent="bg-emerald-500/10" />
        <KpiCard label="Obligations" value={fmtCur(oblSum.total_outstanding)} sub={`${oblSum.total_count} pending`} icon={ArrowDownRight} color="text-rose-400" accent="bg-rose-500/10" />
        <KpiCard
          label="Cash Runway"
          value={
            runway.days_until_negative
              ? `${runway.days_until_negative}d`
              : runway.is_sustainable === true ? 'Stable' : '—'
          }
          sub={
            runway.is_sustainable === true
              ? 'Positive throughout'
              : runway.is_sustainable === false
                ? `Min: ${fmtCur(runway.min_balance)}`
                : 'Cash runway unavailable — opening balance unknown'
          }
          icon={runway.is_sustainable === true ? CheckCircle2 : runway.is_sustainable === false ? AlertTriangle : Info}
          color={runway.is_sustainable === true ? 'text-emerald-400' : runway.is_sustainable === false ? 'text-rose-400' : 'text-amber-400'}
          accent={runway.is_sustainable === true ? 'bg-emerald-500/10' : runway.is_sustainable === false ? 'bg-rose-500/10' : 'bg-amber-500/10'}
        />
        <KpiCard label="Stress Days" value={stress.stress_day_count} sub={stress.negative_day_count > 0 ? `${stress.negative_day_count} negative` : 'No negative days'} icon={ShieldAlert} color={stress.stress_day_count > 0 ? 'text-amber-400' : 'text-emerald-400'} accent={stress.stress_day_count > 0 ? 'bg-amber-500/10' : 'bg-emerald-500/10'} />
      </div>

      {/* Drivers */}
      {drivers.length > 0 && (
        <Card className="bg-zinc-900/40 border-zinc-800/40" data-testid="forecast-drivers">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="text-xs font-semibold text-zinc-400 flex items-center gap-1.5">
              <MessageSquare className="w-3.5 h-3.5 text-sky-400" /> Forecast Drivers
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-3 space-y-1.5">
            {drivers.map((d, i) => {
              const sev = { CRITICAL: 'text-rose-400 bg-rose-500/10 border-rose-500/20', WARNING: 'text-amber-400 bg-amber-500/10 border-amber-500/20', INFO: 'text-sky-400 bg-sky-500/10 border-sky-500/20' };
              const cls = sev[d.severity] || sev.INFO;
              return (
                <div key={`${d.type}-${i}`} className={`flex items-start gap-2 px-3 py-2 rounded-lg border ${cls}`}>
                  <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                  <span className="text-[11px]">{d.message}</span>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Timeline Chart */}
        <div className="lg:col-span-2">
          <Card className="bg-zinc-900/40 border-zinc-800/40" data-testid="forecast-timeline">
            <CardHeader className="pb-2 pt-3 px-4">
              <CardTitle className="text-xs font-semibold text-zinc-400">Projected Cash Balance</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-3">
              <div className="flex items-center gap-4 mb-2">
                <span className="flex items-center gap-1 text-[9px] text-zinc-500"><span className="w-2 h-2 rounded-sm bg-emerald-500 inline-block" /> Positive</span>
                <span className="flex items-center gap-1 text-[9px] text-zinc-500"><span className="w-2 h-2 rounded-sm bg-rose-500 inline-block" /> Negative</span>
                <span className="flex items-center gap-1 text-[9px] text-zinc-500"><span className="w-2 h-2 rounded-sm bg-amber-500/40 inline-block" /> Stress</span>
              </div>
              <div className="space-y-0.5">
                {forecast.map((d) => {
                  const balPct = Math.min(Math.abs(d.closing_balance) / maxBal * 100, 100);
                  const isNeg = d.closing_balance < 0;
                  return (
                    <div key={d.date} className={`flex items-center gap-2 py-0.5 px-1 rounded group transition-colors ${d.is_stress ? 'bg-amber-500/5' : 'hover:bg-zinc-800/30'}`}>
                      <span className="text-[9px] text-zinc-500 w-14 flex-shrink-0 font-mono">{fmtDateShort(d.date)}</span>
                      <div className="flex-1 h-3 rounded-sm overflow-hidden bg-zinc-800/40 relative">
                        <div className={`h-full rounded-sm ${isNeg ? 'bg-rose-500' : 'bg-emerald-500/70'}`} style={{ width: `${balPct}%` }} />
                        {d.is_stress && !isNeg && <div className="absolute inset-0 bg-amber-500/10" />}
                      </div>
                      <span className={`text-[9px] w-16 text-right font-mono ${isNeg ? 'text-rose-400' : 'text-emerald-400'}`}>{fmtCur(d.closing_balance)}</span>
                      <div className="w-32 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-[8px] text-zinc-600">
                        +{fmtCur(d.projected_inflow)} -{fmtCur(d.projected_outflow)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Revenue Sources + Stress */}
        <div className="space-y-3">
          {/* Revenue Source Patterns */}
          <Card className="bg-zinc-900/40 border-zinc-800/40" data-testid="source-patterns">
            <CardHeader className="pb-2 pt-3 px-4">
              <CardTitle className="text-xs font-semibold text-zinc-400 flex items-center gap-1.5">
                <Layers className="w-3.5 h-3.5 text-emerald-400" /> Revenue Sources
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-3 space-y-1.5">
              {patterns.map((p, i) => (
                <div key={`${p.source}-${p.rail}-${i}`} className="flex items-center justify-between py-1">
                  <div>
                    <span className="text-[10px] text-zinc-300">{p.source}</span>
                    <span className="text-[8px] text-zinc-600 ml-1">({p.rail})</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-zinc-400">{fmtCur(p.daily_average)}/d</span>
                    <StatusBadge status={p.status} />
                    <span className={`text-[8px] px-1 py-0.5 rounded ${p.confidence >= 0.7 ? 'text-emerald-400 bg-emerald-500/10' : 'text-amber-400 bg-amber-500/10'}`}>{Math.round(p.confidence * 100)}%</span>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Confidence Breakdown */}
          {confidence.penalties && confidence.penalties.length > 0 && (
            <Card className="bg-zinc-900/40 border-zinc-800/40" data-testid="confidence-breakdown">
              <CardHeader className="pb-2 pt-3 px-4">
                <CardTitle className="text-xs font-semibold text-zinc-400 flex items-center gap-1.5">
                  <Percent className="w-3.5 h-3.5 text-amber-400" /> Confidence Penalties
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-3 space-y-1">
                {confidence.penalties.map((p, i) => (
                  <div key={`${p.factor}-${i}`} className="flex items-center justify-between text-[10px]">
                    <span className="text-zinc-400">{p.detail}</span>
                    <span className="text-rose-400 font-medium">-{p.penalty}%</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Stress Days */}
          {stress.stress_days && stress.stress_days.length > 0 && (
            <Card className="bg-zinc-900/40 border-zinc-800/40" data-testid="stress-days">
              <CardHeader className="pb-2 pt-3 px-4">
                <CardTitle className="text-xs font-semibold text-zinc-400 flex items-center gap-1.5">
                  <ShieldAlert className="w-3.5 h-3.5 text-amber-400" /> Stress Detection
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-3 space-y-1.5">
                {stress.stress_days.map((sd, i) => (
                  <div key={`stress-${i}`} className="px-3 py-1.5 rounded-lg border border-amber-500/20 bg-amber-500/5">
                    <p className="text-[10px] text-amber-400 font-medium">{fmtDate(sd.date)}</p>
                    {sd.reasons.map((r, j) => (
                      <p key={j} className="text-[9px] text-zinc-500 ml-2">- {r}</p>
                    ))}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════
// ACCOUNTS TAB
// ═══════════════════════════════════════════════════

const AccountsTab = ({ accounts, loading, onRefresh, onCreateAccount }) => {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ account_name: '', account_type: 'BANK', provider: '', opening_balance: 0 });
  const [saving, setSaving] = useState(false);

  const handleCreate = async () => {
    if (!form.account_name.trim()) {
      toast.error('Account name is required');
      return;
    }
    setSaving(true);
    try {
      await onCreateAccount({ ...form, opening_balance: parseFloat(form.opening_balance) || 0 });
      setShowForm(false);
      setForm({ account_name: '', account_type: 'BANK', provider: '', opening_balance: 0 });
      toast.success('Account created');
    } catch {
      toast.error('Failed to create account');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-xs text-zinc-500">Cash and bank accounts for movement tracking</p>
        <Button
          size="sm"
          onClick={() => setShowForm(!showForm)}
          className="h-7 text-xs gap-1"
          data-testid="add-account-btn"
        >
          <Plus className="w-3 h-3" /> Add Account
        </Button>
      </div>

      {/* Create Form */}
      {showForm && (
        <Card className="bg-zinc-900/40 border-zinc-800/40" data-testid="account-form">
          <CardContent className="p-4 space-y-3">
            <p className="text-xs font-semibold text-zinc-300">New Account</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-zinc-500 uppercase mb-1 block">Name</label>
                <Input
                  className="h-8 text-xs bg-zinc-900/60 border-zinc-800"
                  value={form.account_name}
                  onChange={(e) => setForm(f => ({ ...f, account_name: e.target.value }))}
                  placeholder="e.g. HDFC Current Account"
                  data-testid="account-name-input"
                />
              </div>
              <div>
                <label className="text-[10px] text-zinc-500 uppercase mb-1 block">Type</label>
                <div className="flex gap-2">
                  {ACCOUNT_TYPES.map((t) => (
                    <button
                      key={t}
                      onClick={() => setForm(f => ({ ...f, account_type: t }))}
                      className={`px-3 py-1.5 rounded text-[10px] font-medium border transition-colors ${
                        form.account_type === t
                          ? 'bg-zinc-700 text-zinc-200 border-zinc-600'
                          : 'bg-zinc-900/40 text-zinc-500 border-zinc-800/40 hover:border-zinc-700'
                      }`}
                      data-testid={`account-type-${t.toLowerCase()}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[10px] text-zinc-500 uppercase mb-1 block">Provider</label>
                <Input
                  className="h-8 text-xs bg-zinc-900/60 border-zinc-800"
                  value={form.provider}
                  onChange={(e) => setForm(f => ({ ...f, provider: e.target.value }))}
                  placeholder="e.g. HDFC Bank"
                  data-testid="account-provider-input"
                />
              </div>
              <div>
                <label className="text-[10px] text-zinc-500 uppercase mb-1 block">Opening Balance</label>
                <Input
                  className="h-8 text-xs bg-zinc-900/60 border-zinc-800"
                  type="number"
                  value={form.opening_balance}
                  onChange={(e) => setForm(f => ({ ...f, opening_balance: e.target.value }))}
                  data-testid="account-balance-input"
                />
              </div>
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Button size="sm" className="h-7 text-xs" onClick={handleCreate} disabled={saving} data-testid="save-account-btn">
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
              </Button>
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Account List */}
      {(!accounts || accounts.length === 0) ? (
        <EmptyState
          icon={Landmark}
          title="No Accounts Configured"
          description="Add bank or cash accounts to track balances across settlement rails. This is optional for Phase 1."
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="accounts-list">
          {accounts.map((acc) => {
            const typeIcon = acc.account_type === 'CASH' ? Banknote : acc.account_type === 'WALLET' ? Wallet : Landmark;
            const TypeIcon = typeIcon;
            return (
              <Card key={acc.account_id} className="bg-zinc-900/40 border-zinc-800/40" data-testid={`account-card-${acc.account_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <div className="p-2 rounded-lg bg-zinc-800/60">
                        <TypeIcon className="w-4 h-4 text-zinc-400" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-zinc-200">{acc.account_name}</p>
                        <p className="text-[10px] text-zinc-500">{acc.provider || acc.account_type}</p>
                      </div>
                    </div>
                    <Badge variant="outline" className={`text-[9px] ${acc.is_active ? 'border-emerald-500/30 text-emerald-400' : 'border-zinc-700 text-zinc-500'}`}>
                      {acc.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-[10px] text-zinc-500">Opening Balance</span>
                    <span className="text-sm font-semibold text-zinc-300">{fmtCur(acc.opening_balance)}</span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════

export default function CashFlowPage() {
  const [activeTab, setActiveTab] = useState('overview');
  const periodState = usePeriod(PERIOD_MODES.RANGE);
  const dateFrom = periodState.period.from;
  const dateTo = periodState.period.to;

  // Overview state
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);

  // Ledger state
  const [ledger, setLedger] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerFilters, setLedgerFilters] = useState({ direction: '', status: '', search: '' });

  // Accounts state
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);

  const [rebuilding, setRebuilding] = useState(false);
  const [statusKey, setStatusKey] = useState(0);

  // ── Data fetchers ──
  const fetchOverview = useCallback(async () => {
    setOverviewLoading(true);
    try {
      const res = await accountsAPI.getCashflowOverview(periodState.apiParams);
      setOverview(res.data);
    } catch (err) {
      console.error('Overview fetch error:', err);
      toast.error('Failed to load cash flow overview');
    } finally {
      setOverviewLoading(false);
    }
  }, [dateFrom, dateTo, periodState.apiParams]);

  const fetchLedger = useCallback(async () => {
    setLedgerLoading(true);
    try {
      const params = { date_from: dateFrom, date_to: dateTo };
      if (ledgerFilters.direction) params.direction = ledgerFilters.direction;
      if (ledgerFilters.status) params.status = ledgerFilters.status;
      if (ledgerFilters.search) params.search = ledgerFilters.search;
      const res = await accountsAPI.getCashflowLedger(params);
      setLedger(res.data);
    } catch (err) {
      console.error('Ledger fetch error:', err);
      toast.error('Failed to load ledger');
    } finally {
      setLedgerLoading(false);
    }
  }, [dateFrom, dateTo, ledgerFilters]);

  const fetchAccounts = useCallback(async () => {
    setAccountsLoading(true);
    try {
      const res = await accountsAPI.listCashAccounts();
      setAccounts(res.data.accounts || []);
    } catch (err) {
      console.error('Accounts fetch error:', err);
    } finally {
      setAccountsLoading(false);
    }
  }, []);

  const handleRebuild = useCallback(async () => {
    setRebuilding(true);
    try {
      const res = await accountsAPI.rebuildCashMovements({ date_from: dateFrom, date_to: dateTo });
      const d = res.data;
      const s = d.rebuild_summary || {};
      if (s.batch_status === 'INCOMPLETE') toast.warning(`Rebuild incomplete: ${(s.outlets || []).filter((o) => o.status !== 'COMPLETED').map((o) => `${o.outlet_id} ${o.status}`).join(', ')}`);
      else toast.success(`Rebuild ${d.action}${s.movements_created !== undefined ? ` — ${s.movements_created} movements published` : ''}`);
      setStatusKey((k) => k + 1);
      // Refresh current tab
      if (activeTab === 'overview') fetchOverview();
      else if (activeTab === 'ledger') fetchLedger();
    } catch (e) {
      const det = e.response?.data?.detail;
      toast.error(det?.code ? `${det.code}: ${det.message}` : 'Rebuild failed');
      setStatusKey((k) => k + 1);
    } finally {
      setRebuilding(false);
    }
  }, [dateFrom, dateTo, activeTab, fetchOverview, fetchLedger]);

  const handleCreateAccount = useCallback(async (data) => {
    await accountsAPI.createCashAccount(data);
    fetchAccounts();
  }, [fetchAccounts]);

  // ── Effects ──
  useEffect(() => {
    if (activeTab === 'overview') fetchOverview();
    else if (activeTab === 'ledger') fetchLedger();
    else if (activeTab === 'accounts') fetchAccounts();
  }, [activeTab, fetchOverview, fetchLedger, fetchAccounts]);

  // Debounced search for ledger
  useEffect(() => {
    if (activeTab !== 'ledger') return;
    const timer = setTimeout(() => { fetchLedger(); }, 400);
    return () => clearTimeout(timer);
  }, [ledgerFilters.search]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-4 max-w-[1400px] mx-auto" data-testid="cashflow-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-zinc-100 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-sky-400" /> Cash Flow Engine
          </h1>
          <p className="text-xs text-zinc-500 mt-0.5">Derived cash movements — realized vs expected, with full provenance</p>
        </div>

        <div className="flex items-center gap-2">
          <PeriodToolbar state={periodState} mode={PERIOD_MODES.RANGE} resolved={overview} />

          {/* Rebuild */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleRebuild}
            disabled={rebuilding}
            className="h-7 text-xs gap-1 border-zinc-700"
            data-testid="rebuild-btn"
          >
            {rebuilding ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            Rebuild
          </Button>
        </div>
      </div>

      <CashProjectionStatusPanel dateFrom={dateFrom} dateTo={dateTo} refreshKey={statusKey} />

      {/* Tab Navigation */}
      <div className="flex items-center gap-1 p-1 bg-zinc-900/40 rounded-lg border border-zinc-800/40 w-fit" data-testid="tab-nav">
        {TABS.map((tab) => {
          const TabIcon = tab.icon;
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                isActive
                  ? 'bg-zinc-800 text-zinc-100 shadow-sm'
                  : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/40'
              }`}
              data-testid={`tab-${tab.key}`}
            >
              <TabIcon className="w-3.5 h-3.5" /> {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && <OverviewTab data={overview} loading={overviewLoading} />}
      {activeTab === 'ledger' && (
        <LedgerTab data={ledger} loading={ledgerLoading} filters={ledgerFilters} setFilters={setLedgerFilters} />
      )}
      {activeTab === 'reconciliation' && (
        <ReconciliationTab dateFrom={dateFrom} dateTo={dateTo} />
      )}
      {activeTab === 'obligations' && <ObligationsTab />}
      {activeTab === 'forecast' && <ForecastTab />}
      {activeTab === 'accounts' && (
        <AccountsTab
          accounts={accounts}
          loading={accountsLoading}
          onRefresh={fetchAccounts}
          onCreateAccount={handleCreateAccount}
        />
      )}
    </div>
  );
}
